import { createContext, useContext, useEffect, useMemo, useState, ReactNode, useRef } from 'react';
import type { CommonHTMLAttributes } from '@frontend/types';
import { KeyNames } from '../../../constants/key-names';
import { usePortal, type UsePortalProps } from '../../../hooks/use-portal';
import { useTabbable } from '../../../hooks/use-tabbable';
import { useUid } from '../../../hooks/use-uid';
import { useModalAppConfigContext } from './modal-app-config-provider';
import type { ModalControlModalProps } from './use-modal-control';

type AriaModalValue = 'true' | 'false';

export type ModalProviderContextType = Pick<ModalControlModalProps, 'show' | 'onClose'> & {
  containerProps: {
    role: string;
    'aria-labelledby': string;
    'aria-describedby': string;
    'aria-modal': AriaModalValue;
  };
  descriptionId: string;
  loadingState: LoadingState;
  setLoading: (loading: boolean, message?: string) => void;
  titleId: string;
  uid: string;
  actionStateChanged: { primary: boolean; secondary: boolean };
  focusTimeout: number;
  /**
   * True when the app opted in via `Modal.AppConfigProvider pinToViewport` and the modal renders straight into
   * `<body>`. Parts positioned against the page should then pin to the viewport, so the modal opens on screen even
   * when the window is scrolled.
   */
  pinToViewport: boolean;
  setActionStateChanged: ({ primary, secondary }: { primary: boolean; secondary: boolean }) => void;
};

export const ModalProviderContext = createContext<ModalProviderContextType>({} as ModalProviderContextType);

export type ModalProviderProps = Pick<ModalControlModalProps, 'show' | 'onClose'> &
  Partial<Pick<UsePortalProps, 'prepend' | 'mountTarget'>> & {
    children: ReactNode;
    className?: string;
    containerAttributes?: Omit<CommonHTMLAttributes, 'style'> & { style?: string };
    disableCloseOnEscape?: boolean;
    timeout?: number;
  };

type LoadingState = {
  show: boolean;
  message?: string;
};

// Global config for modal settings
// Allows making global changes to where/how modals mount
type GlobalModalConfig = {
  mountTarget?: string | (() => string | undefined);
  prepend?: boolean;
};

let GLOBAL_MODAL_CONFIG: GlobalModalConfig = {};

export function configureModalSettings(settings: GlobalModalConfig) {
  GLOBAL_MODAL_CONFIG = settings;
}

const getConfigMountTarget = () => {
  return typeof GLOBAL_MODAL_CONFIG.mountTarget === 'function'
    ? GLOBAL_MODAL_CONFIG?.mountTarget()
    : GLOBAL_MODAL_CONFIG.mountTarget;
};

const getMountTarget = (selector?: string): HTMLElement => {
  const configMountTarget = getConfigMountTarget();
  const target = selector || configMountTarget;
  if (target) {
    return document.querySelector(target) ?? document.body;
  }
  return document.body;
};

/** Marks a portal root rendered into `<body>`; container styles switch to `position: fixed` on it. */
const BODY_MOUNT_TARGET_ATTRIBUTE = 'data-mount-target';

export const ModalProvider = ({
  children,
  className,
  containerAttributes,
  disableCloseOnEscape,
  onClose,
  mountTarget,
  prepend,
  show,
  timeout = 0,
}: ModalProviderProps) => {
  const uid = useUid();
  const [loadingState, setLoadingState] = useState<LoadingState>({
    show: false,
    message: '',
  });
  const [actionStateChanged, setActionStateChanged] = useState({ primary: false, secondary: false });
  // Opt-in per app; read once, on open. Off keeps the original (absolute, scroll-on-focus) behavior.
  const appConfig = useModalAppConfigContext();
  const [pinToViewportEnabled] = useState(() => !!appConfig?.pinToViewport);
  // Initial guess for the first render; corrected below once the portal root is actually inserted.
  const [isMountedOnBody, setIsMountedOnBody] = useState(
    () => typeof document !== 'undefined' && getMountTarget(mountTarget) === document.body
  );
  const pinToViewport = pinToViewportEnabled && isMountedOnBody;
  const { Portal, portalRef } = usePortal({
    attributes: {
      ...containerAttributes,
      id: `modal=${uid.current}`,
      tabIndex: -1,
    },
    className,
    mountTarget: mountTarget || getConfigMountTarget(),
    prepend: prepend ?? GLOBAL_MODAL_CONFIG.prepend,
  });

  // `usePortal` resolves its target in an effect (declared above, so it runs first), and a custom target may only
  // exist by then. Derive the body mount from where the root actually landed.
  useEffect(() => {
    const root = portalRef.current;
    if (!root || !pinToViewportEnabled) return;
    const mountedOnBody = root.parentElement === document.body;
    if (mountedOnBody) {
      root.setAttribute(BODY_MOUNT_TARGET_ATTRIBUTE, 'body');
    } else {
      root.removeAttribute(BODY_MOUNT_TARGET_ATTRIBUTE);
    }
    setIsMountedOnBody(mountedOnBody);
  }, []);

  useTabbable({ ref: portalRef, timeout, reset: actionStateChanged });

  const contextValue = useMemo(() => {
    const descriptionId = `modal=${uid.current}-desc`;
    const titleId = `modal=${uid.current}-title`;

    return {
      containerProps: {
        role: 'dialog',
        'aria-labelledby': titleId,
        'aria-describedby': descriptionId,
        'aria-modal': 'true' as AriaModalValue,
      },
      descriptionId,
      loadingState,
      show,
      onClose,
      setLoading: (loading: boolean, message?: string) => {
        setLoadingState({ show: loading, message });
      },
      titleId,
      uid: uid.current,
      actionStateChanged,
      focusTimeout: timeout,
      pinToViewport,
      setActionStateChanged,
    };
  }, [onClose, loadingState, setLoadingState, show, timeout, pinToViewport]);

  const overflowValue = useRef<string>();

  // setup and cleanup
  useEffect(() => {
    const container = getMountTarget(mountTarget);
    // disable container scrolling to avoid funkity funk
    overflowValue.current = container.style.overflow;
    container.style.overflow = 'hidden';
    // focus first on the modal (reads title and description to screen readers)
    // useTabbable will trap subsequent tabbing to within the modal,
    // but the modal itself should only be focused on mount.
    // `preventScroll` (opted-in apps, body mounts only): browsers (notably iOS Safari) otherwise scroll the page to the
    // focused container. Custom targets keep the default so a modal inside a scrolled container is brought into view.
    setTimeout(() => {
      const root = portalRef.current;
      root?.focus({ preventScroll: pinToViewportEnabled && root.parentElement === document.body });
    }, timeout);

    return () => {
      // when two modals are open, we don't want to re-enable scrolling until the all modals are closed
      if (!container.querySelectorAll(':scope > [id^="modal="]').length) {
        // re-enable container scrolling
        container.style.overflow = overflowValue.current === 'hidden' ? '' : overflowValue.current ?? '';
      }
    };
  }, []);

  useEffect(() => {
    const closeOnEsc = (event) => {
      if (event.key === KeyNames.Escape) {
        event.stopPropagation();
        onClose();
      }
    };
    // prevent esc to close manually, or while in a loading state
    if (!disableCloseOnEscape && !loadingState.show) {
      portalRef.current?.addEventListener('keydown', closeOnEsc);
    }
    return () => {
      portalRef.current?.removeEventListener('keydown', closeOnEsc);
    };
  }, [disableCloseOnEscape, loadingState.show]);

  return (
    <ModalProviderContext.Provider value={contextValue}>
      <Portal>{children}</Portal>
    </ModalProviderContext.Provider>
  );
};

export function useModalContext() {
  const context = useContext(ModalProviderContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used inside a ModalProvider.');
  }
  return context;
}

export function useModalContextControl() {
  const context = useContext(ModalProviderContext);
  if (context === undefined) {
    throw new Error('useModalContext must be used inside a ModalProvider.');
  }
  return {
    closeModal: context.onClose,
    setLoading: context.setLoading,
    loading: context.loadingState.show,
    message: context.loadingState.message,
  };
}

export function useModalLoadingState() {
  const context = useContext(ModalProviderContext);
  if (context === undefined) {
    throw new Error('useModalLoadingState must be used inside a ModalProvider.');
  }
  const { loadingState, setLoading } = context;
  return {
    loading: loadingState.show,
    message: loadingState.message,
    setLoading,
  };
}
